import React from 'react';
import { Check } from 'lucide-react';
import { featureGroups, trustBuilderContent } from './landingContent';

function useEqualHeightTiles(count: number) {
  const refs = React.useRef<Array<HTMLDivElement | null>>([]);
  const [height, setHeight] = React.useState<number | null>(null);

  const setRef = React.useCallback(
    (idx: number) => (el: HTMLDivElement | null) => {
      refs.current[idx] = el;
    },
    []
  );

  const recompute = React.useCallback(() => {
    const els = refs.current.filter(Boolean) as HTMLDivElement[];
    if (!els.length) return;
    const max = Math.max(...els.map((el) => Math.ceil(el.getBoundingClientRect().height)));
    setHeight(max || null);
  }, []);

  React.useLayoutEffect(() => {
    refs.current = refs.current.slice(0, count);
    recompute();

    const els = refs.current.filter(Boolean) as HTMLDivElement[];
    if (!els.length) return;

    const ro = new ResizeObserver(() => recompute());
    els.forEach((el) => ro.observe(el));

    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [count, recompute]);

  return { height, setRef };
}

export const TrustBuilderSection: React.FC = () => {
  const { heading, subheading, bullets, closingLine } = trustBuilderContent;
  const totalTiles = featureGroups.reduce((sum, g) => sum + g.items.length, 0);
  const { height: equalTileHeight, setRef: setTileRef } = useEqualHeightTiles(totalTiles);

  return (
    <section className="px-4 py-12 md:py-16 bg-[#3A3C3E] text-white relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-60"
        aria-hidden
        style={{
          background:
            'radial-gradient(700px 240px at 10% 20%, rgba(255,147,79,0.22), transparent 60%), radial-gradient(600px 220px at 90% 30%, rgba(46,139,87,0.22), transparent 55%)',
        }}
      />
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center mb-6 md:mb-8">
          <div className="h-2 w-32 rounded-full bg-gradient-to-r from-primary/70 via-secondary/70 to-primary/30" />
        </div>

        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-primary">
          {heading}
        </h2>
        <p className="text-center text-white/80 text-lg mb-10 leading-relaxed max-w-3xl mx-auto">
          {subheading}
        </p>

        {/* Tools offering (moved here from Explore section) */}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-10 md:mb-12">
          {featureGroups.map((group, groupIdx) => {
            const baseIndex = featureGroups
              .slice(0, groupIdx)
              .reduce((sum, g) => sum + g.items.length, 0);

            return (
              <div key={group.title} className="flex flex-col min-w-0">
              <div className="mb-4 text-center lg:text-left">
                <h4 className="text-xl md:text-2xl font-bold text-primary mb-2">{group.title}</h4>
                <p className="text-white/80 text-sm md:text-base leading-relaxed">{group.description}</p>
              </div>
              <div className="flex flex-col gap-3 flex-1">
                {group.items.map((feature, idx) => (
                  <div
                    key={feature.title}
                    ref={setTileRef(baseIndex + idx)}
                    style={equalTileHeight ? { height: equalTileHeight } : undefined}
                    className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors p-4"
                  >
                    <div className="flex items-center gap-3 h-full">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary/25 to-secondary/15 rounded-xl flex items-center justify-center text-xl flex-shrink-0 self-center">
                        {feature.icon}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-semibold text-sm leading-snug">{feature.title}</p>
                        <p className="text-white/70 text-xs md:text-sm leading-snug mt-0.5">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 shadow-card">
          <ul className="space-y-3">
            {bullets.map((line) => (
              <li key={line} className="flex gap-3">
                <Check
                  className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5"
                  strokeWidth={2.5}
                  aria-hidden
                />
                <span className="text-white/90 text-sm md:text-base leading-relaxed">
                  {line}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-center text-lg font-semibold text-white pt-6 md:pt-8">
          {closingLine}
        </p>
      </div>
    </section>
  );
};
